import {
  FetchError,
  POST,
  getErrorMessage,
  isErrorResponse,
  logger,
  request,
  toBig,
  useDebouncedValue,
  useEndpointsContext,
  type RestResponse,
} from '@talos/kyoko';
import { useEffect, useMemo, useState } from 'react';

interface WithdrawalPreview {
  RequestedQuantity: string;
  WithdrawalFee: string;
  TransferQuantity: string;
  Currency: string;
}

interface WithdrawalPreviewArgs {
  currency?: string;
  amount?: string;
  enabled: boolean;
}

export function useWithdrawalPreview({ currency, amount, enabled }: WithdrawalPreviewArgs) {
  const { apiEndpoint } = useEndpointsContext();
  const previewEndpoint = `${apiEndpoint}/transactions/withdrawal/preview`;
  const input = useMemo(
    () =>
      enabled && currency && amount && toBig(amount)?.gt(0) ? { Currency: currency, Quantity: amount } : undefined,
    [enabled, currency, amount]
  );
  const debouncedInput = useDebouncedValue(input);
  const [result, setResult] = useState<{ input: typeof input; preview: WithdrawalPreview }>();

  useEffect(() => {
    if (!input || input !== debouncedInput) {
      return;
    }

    let cancelled = false;
    request<RestResponse<WithdrawalPreview> | null>(POST, previewEndpoint, input, {
      skippedErrorReportingStatusCodes: [404],
    })
      .then(response => {
        if (cancelled || response == null) {
          return;
        }
        if (isErrorResponse(response)) {
          throw new Error(getErrorMessage(response) ?? 'Withdrawal preview failed');
        }
        const preview = response.data?.at(0);
        if (
          !preview ||
          typeof preview.RequestedQuantity !== 'string' ||
          preview.RequestedQuantity === '' ||
          typeof preview.WithdrawalFee !== 'string' ||
          preview.WithdrawalFee === '' ||
          typeof preview.TransferQuantity !== 'string' ||
          preview.TransferQuantity === ''
        ) {
          return;
        }
        if (
          preview.Currency !== input.Currency ||
          !toBig(preview.RequestedQuantity)?.eq(input.Quantity) ||
          !toBig(preview.WithdrawalFee)?.gte(0) ||
          !toBig(preview.TransferQuantity)?.gt(0)
        ) {
          throw new Error('Invalid withdrawal preview response');
        }
        setResult({ input, preview });
      })
      .catch((err: unknown) => {
        if (cancelled || err instanceof FetchError) {
          // HTTP errors are reported by request; 404 is expected on older deployments.
          return;
        }
        logger.warn('Failed to fetch withdrawal preview', { err });
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedInput, input, previewEndpoint]);

  return input && result?.input === input ? result.preview : undefined;
}
